import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/Parent-communication.svg";
import WhatsAppButton from "../components/WhatsappButton";
import RondleidingForm from "../components/RondleidingForm";

const ParentCollaboration: React.FC = () => {
  const { t } = useLanguage();
    const [openForm, setOpenForm] = React.useState<null | "rondleiding">(null);

  const partnershipPillars = t("parentCollaboration.partnershipPillars");
  const practiceAreas = t("parentCollaboration.practiceAreas");
  const feedbackMethods = t("parentCollaboration.feedbackMethods");
  const parentCommitteeAreas = t("parentCollaboration.parentCommitteeAreas");

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative overflow-hidden hero-section-h">
          <div className="absolute inset-0 pointer-events-none z-0">
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[280px] w-full opacity-100"
            />
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-0 right-0 max-w-[250px] sm:max-w-[320px] md:max-w-[380px] lg:max-w-[520px] w-full opacity-100"
            />
          </div>
          <div className="relative container mx-auto px-4 py-20 z-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-center text-black mb-6">
              {t("parentCollaboration.hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-center text-natural-800 opacity-90 max-w-4xl mx-auto leading-relaxed">
              {t("parentCollaboration.hero.subtitle")}
            </p>
          </div>
        </div>

        {/* Introduction */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 md:p-12 mb-16">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-6 leading-tight">
                  {t("parentCollaboration.intro.title")}
                </h2>
                <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                  {t("parentCollaboration.intro.text1")}
                </p>
                <p className="text-lg text-neutral-800 leading-relaxed">
                  {t("parentCollaboration.intro.text2")}
                </p>
              </div>
              <div className="bg-amber-50 rounded-2xl p-8 mt-6">
                <h3 className="text-2xl font-semibold text-neutral-900 mb-6">
                  {t("parentCollaboration.intro.supportTitle")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("parentCollaboration.intro.supportText")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Partnership Pillars */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("parentCollaboration.pillars.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("parentCollaboration.pillars.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {partnershipPillars.map((pillar: any, index: number) => (
              <div key={index} className="group">
                <div
                  className={`${pillar.color} rounded-2xl p-8 text-white text-center h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                >
                  <div className="text-5xl mb-6">{pillar.icon}</div>
                  <h3 className="text-xl font-semibold mb-4">{pillar.title}</h3>
                  <p className="text-lg opacity-90 leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Practice Areas */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("parentCollaboration.practice.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("parentCollaboration.practice.subtitle")}
            </p>
          </div>
          <div className="space-y-12 max-w-7xl mx-auto">
            {practiceAreas.map((area: any, index: number) => (
              <div
                key={index}
                className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="grid lg:grid-cols-3 gap-8 items-start">
                  <div className="text-center lg:text-left">
                    <div className="text-6xl mb-4">{area.icon}</div>
                    <h3 className="text-2xl font-semibold text-neutral-900 mb-2">
                      {area.title}
                    </h3>
                    <div className="inline-block bg-[#492A19] text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                      {area.subtitle}
                    </div>
                    <p className="text-lg text-neutral-800 leading-relaxed">
                      {area.description}
                    </p>
                  </div>
                  <div className="lg:col-span-2 my-auto">
                    <div className="bg-amber-50 rounded-xl p-6">
                      <h4 className="text-xl font-semibold text-neutral-900 mb-4">
                        {t("parentCollaboration.practice.featuresLabel")}
                      </h4>
                      <ul className="space-y-3 mx-auto my-auto w-fit">
                        {area.features.map((feature: string, idx: number) => (
                          <li key={idx} className="flex items-start text-left space-x-3">
                            <div className="w-2 h-2 bg-[#492a19] rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-neutral-800">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feedback Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#663b23] rounded-3xl shadow-2xl p-12 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-black opacity-5"></div>
              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-semibold text-center mb-8">
                  {t("parentCollaboration.feedback.title")}
                </h2>
                <p className="text-xl mb-8 text-center opacity-90 leading-relaxed">
                  {t("parentCollaboration.feedback.subtitle")}
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {feedbackMethods.map((method: any, index: number) => (
                    <div
                      key={index}
                      className="bg-[#FBEACD] bg-opacity-10 rounded-xl p-6 backdrop-blur-sm text-center"
                    >
                      <div className="text-4xl mb-4">{method.icon}</div>
                      <h3 className="text-lg font-semibold mb-2">
                        {method.title}
                      </h3>
                      <p className="text-md opacity-90">{method.description}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center mt-8 text-lg opacity-90">
                  {t("parentCollaboration.feedback.note")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Parent Committee */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("parentCollaboration.committee.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("parentCollaboration.committee.subtitle")}
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-2xl font-semibold text-neutral-900 mb-6">
                    {t("parentCollaboration.committee.activeRole")}
                  </h3>
                  <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                    {t("parentCollaboration.committee.text1")}
                  </p>
                  <p className="text-lg text-neutral-800 leading-relaxed">
                    {t("parentCollaboration.committee.text2")}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-2xl p-8">
                  <h4 className="text-xl font-semibold text-neutral-900 mb-6">
                    {t("parentCollaboration.committee.areasTitle")}
                  </h4>
                  <ul className="space-y-4 mx-auto my-auto w-full max-w-72 text-left ml-6 md:ml-20">
                    {parentCommitteeAreas.map((area: string, index: number) => (
                      <li key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#492A19] rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-neutral-800">{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="container mx-auto px-4 py-16 font-child">
          <div className="text-center">
            <div className="rounded-3xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
                {t("parentCollaboration.cta.title")}
              </h3>
              <p className="text-xl text-neutral-800 mb-8 max-w-2xl mx-auto leading-relaxed">
                {t("parentCollaboration.cta.subtitle")}
              </p>
              <button
                className="bg-[#FBEACD] text-[#492A19] px-8 py-3 rounded-full font-semibold text-lg hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
                onClick={() => setOpenForm("rondleiding")}
              >
                {t("parentCollaboration.cta.button")}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Forms Modals */}
      {openForm === "rondleiding" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-6">
          <div className="bg-[#FBEACD] rounded-2xl p-8 max-w-lg w-full relative">
            <button
              onClick={() => setOpenForm(null)}
              className="absolute top-4 right-4 text-2xl"
            >
              &times;
            </button>
            <RondleidingForm />
          </div>
        </div>
      )}
      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default ParentCollaboration;
